/** @format */

import { Switch, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toggle } from 'src/components';
import { CloseIcon } from 'src/components/icons';
import { TopicSearchAutocomplete } from 'src/components/search';
import type { Topic } from 'src/types';
import {
  createNewContentEditorTag,
  selectedTagsIncludeLabel
} from 'src/utils/contentEditor/contentEditorTags';

interface ContentEditorTagOption {
  id: string | number;
  label?: string;
  isNew?: boolean;
}

interface ContentEditorSettingsProps {
  topicTree?: Topic[];
  topicIds?: Array<string | number>;
  topicTreeLoading?: boolean;
  topicTreeError?: string | Record<string, unknown> | null;
  topicsError?: string;
  tagsError?: string;
  onToggleTopic: (topicId: string | number) => void;
  onAddTopicFromSearch: (topicId: string | number) => void;
  tagInputValue?: string;
  onTagInputChange?: (value: string) => void;
  tagOptions?: ContentEditorTagOption[];
  tagLoading?: boolean;
  selectedTags?: ContentEditorTagOption[];
  onSelectedTagsChange: (tags: ContentEditorTagOption[]) => void;
  sendNotification?: boolean;
  onSendNotificationChange: (checked: boolean) => void;
  releaseDate?: string | null;
  onReleaseDateChange?: (date: string | null) => void;
}

export default function ContentEditorSettings({
  topicTree = [],
  topicIds = [],
  topicTreeLoading = false,
  topicTreeError = null,
  topicsError = '',
  tagsError = '',
  onToggleTopic,
  onAddTopicFromSearch,
  tagInputValue = '',
  onTagInputChange,
  tagOptions = [],
  tagLoading = false,
  selectedTags = [],
  onSelectedTagsChange,
  sendNotification = false,
  onSendNotificationChange,
  releaseDate = null,
  onReleaseDateChange
}: ContentEditorSettingsProps) {
  const { t } = useTranslation();
  const tagFieldRef = useRef(null);
  const [isTagAutocompleteOpen, setIsTagAutocompleteOpen] = useState(false);
  const [releaseDatePickerOpen, setReleaseDatePickerOpen] = useState(false);
  /** Suppress focus-driven reopen right after close (date pick, backdrop click, Escape). */
  const skipReleaseDatePickerOpenOnFocusRef = useRef(false);
  const releaseDatePickerFocusGuardTimeoutRef = useRef(null);

  const pickerValue = releaseDate ? moment(releaseDate, 'YYYY-MM-DD', true) : null;
  const safePickerValue = pickerValue && pickerValue.isValid() ? pickerValue : null;

  const tagQueryNormalized = (tagInputValue || '').trim().toLowerCase();

  const tagAutocompleteMatches = useMemo(() => {
    if (!tagQueryNormalized) return [];
    const selectedIds = new Set((selectedTags || []).map(x => String(x.id)));
    return (tagOptions || [])
      .filter(opt => {
        if (opt.isNew) return false;
        const lab = String(opt.label || '').toLowerCase();
        if (!lab || !lab.includes(tagQueryNormalized)) return false;
        if (selectedIds.has(String(opt.id))) return false;
        return true;
      })
      .slice(0, 10);
  }, [tagOptions, tagQueryNormalized, selectedTags]);

  const showTagSuggestionsList =
    isTagAutocompleteOpen && tagQueryNormalized && (tagLoading || tagAutocompleteMatches.length > 0);

  const handleTagSearchChange = event => {
    const next = event.target.value;
    if (typeof onTagInputChange === 'function') {
      onTagInputChange(next);
    }
    setIsTagAutocompleteOpen(Boolean(next.trim()));
  };

  const clearTagInput = () => {
    if (typeof onTagInputChange === 'function') {
      onTagInputChange('');
    }
    setIsTagAutocompleteOpen(false);
  };

  const addTag = option => {
    if (option == null || option.id === undefined || option.id === null) return;
    const exists = (selectedTags || []).some(x => String(x.id) === String(option.id));
    if (exists) return;
    const label = option.label || String(option.id);
    if (selectedTagsIncludeLabel(selectedTags, label)) {
      clearTagInput();
      return;
    }
    onSelectedTagsChange([...(selectedTags || []), { id: option.id, label }]);
    clearTagInput();
  };

  const addNewTag = rawLabel => {
    const nextTag = createNewContentEditorTag(rawLabel);
    if (!nextTag) return;
    if (selectedTagsIncludeLabel(selectedTags, nextTag.label)) {
      clearTagInput();
      return;
    }
    onSelectedTagsChange([
      ...(selectedTags || []),
      { id: nextTag.id, label: nextTag.label, isNew: true }
    ]);
    clearTagInput();
  };

  const handleTagSearchKeyDown = event => {
    if (event.key === 'Escape') {
      setIsTagAutocompleteOpen(false);
      return;
    }
    if (event.key !== 'Enter' && event.key !== 'NumpadEnter') return;
    event.preventDefault();
    if (!tagQueryNormalized) return;
    if (tagLoading) return;
    const exact = (tagOptions || []).find(
      o =>
        !o.isNew &&
        String(o.label || '')
          .trim()
          .toLowerCase() === tagQueryNormalized &&
        !(selectedTags || []).some(s => String(s.id) === String(o.id))
    );
    const first = tagAutocompleteMatches.find(row => !row.isNew);
    const pick = exact || first;
    if (pick) {
      addTag(pick);
      return;
    }
    addNewTag(tagInputValue);
  };

  useEffect(() => {
    return () => {
      if (releaseDatePickerFocusGuardTimeoutRef.current != null) {
        window.clearTimeout(releaseDatePickerFocusGuardTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isTagAutocompleteOpen) return;
    const onDocMouseDown = event => {
      const el = tagFieldRef.current;
      if (!el) return;
      if (el.contains(event.target)) return;
      setIsTagAutocompleteOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [isTagAutocompleteOpen]);

  return (
    <section
      className="content-editor__section content-editor__section--settings"
      aria-labelledby="content-editor-settings-heading">
      <h2 className="content-editor__section-title" id="content-editor-settings-heading">
        {t('common.settings')}
      </h2>

      <div className="content-editor__settings-grid">
        <div className="content-editor__settings-row">
          <div className="content-editor__settings-meta">
            <div className="content-editor__settings-label">
              {t('common.topic')}
              <span className="content-editor__settings-required" aria-hidden>
                *
              </span>
            </div>
            <p className="content-editor__settings-helper">{t('contentEditor.topicHelper')}</p>
          </div>
          <div className="content-editor__settings-field">
            <div className="content-editor__topic-field">
              <TopicSearchAutocomplete
                variant="contentEditor"
                idPrefix="content-editor-topic"
                topicTree={topicTree}
                selectedTopicIds={topicIds}
                onToggleTopic={onToggleTopic}
                onAddTopicFromSearch={onAddTopicFromSearch}
                loading={topicTreeLoading}
                error={topicTreeError}
                loadingMessage={t('common.loading')}
                placeholder={t('contentEditor.topicSearchPlaceholder')}
                inputAriaLabel={t('common.topic')}
                listAriaLabel={t('contentEditor.topicSuggestionsAria')}
                selectedBadgeText={t('contentEditor.topicSuggestionSelected')}
                getRemoveChipAriaLabel={name => t('common.removeItemAria', { name })}
              />
              {topicsError ? (
                <p className="content-editor__field-error" role="alert">
                  {topicsError}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="content-editor__settings-row">
          <div className="content-editor__settings-meta">
            <div className="content-editor__settings-label">
              {t('contentEditor.contentTags')}
              <span className="content-editor__settings-required" aria-hidden>
                *
              </span>
            </div>
            <p className="content-editor__settings-helper">{t('contentEditor.contentTagsHelper')}</p>
          </div>
          <div className="content-editor__settings-field content-editor__settings-field--tags">
            <div className="keyword-search content-editor__tag-search">
              <div className="keyword-search__field content-editor__tag-search-field" ref={tagFieldRef}>
                <input
                  type="search"
                  className="search-page__query-input"
                  value={tagInputValue}
                  onChange={handleTagSearchChange}
                  onKeyDown={handleTagSearchKeyDown}
                  placeholder={t('contentEditor.tagsFieldPlaceholder')}
                  aria-label={t('contentEditor.tags')}
                  aria-autocomplete="list"
                  aria-expanded={Boolean(showTagSuggestionsList)}
                  aria-controls="content-editor-tag-suggestions"
                  autoComplete="off"
                />
                {showTagSuggestionsList ? (
                  <ul
                    id="content-editor-tag-suggestions"
                    className="content-editor__tag-suggestions"
                    role="listbox"
                    aria-label={t('contentEditor.tags')}>
                    {tagLoading && tagAutocompleteMatches.length === 0 ? (
                      <li
                        className="content-editor__tag-suggestion-item content-editor__tag-suggestion-item--loading"
                        role="presentation">
                        <span className="content-editor__tag-search-loading" aria-live="polite">
                          {t('common.loading')}
                        </span>
                      </li>
                    ) : null}
                    {tagAutocompleteMatches.map(row => (
                      <li key={String(row.id)} className="content-editor__tag-suggestion-item" role="none">
                        <button
                          type="button"
                          className="content-editor__tag-suggestion-btn"
                          role="option"
                          aria-selected={false}
                          onClick={() => addTag(row)}>
                          <span className="content-editor__tag-suggestion-label">{row.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
            {(selectedTags || []).length > 0 ? (
              <div className="keyword-search__keywords">
                {(selectedTags || []).map(tag => (
                  <span key={String(tag.id)} className="keyword-search__keyword" aria-label={tag.label}>
                    <span>{tag.label}</span>
                    <button
                      type="button"
                      className="keyword-search__keyword-remove content-editor__tag-keyword-remove"
                      onClick={() =>
                        onSelectedTagsChange((selectedTags || []).filter(x => String(x.id) !== String(tag.id)))
                      }
                      aria-label={t('common.removeItemAria', { name: tag.label || tag.id })}>
                      <CloseIcon width={12} height={12} />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
            {tagsError ? (
              <p className="content-editor__field-error" role="alert">
                {tagsError}
              </p>
            ) : null}
          </div>
        </div>

        <div className="content-editor__settings-row">
          <div className="content-editor__settings-meta">
            <div className="content-editor__settings-label">{t('contentEditor.notifications')}</div>
            <p className="content-editor__settings-helper">{t('contentEditor.notificationsHelper')}</p>
          </div>
          <div className="content-editor__settings-field">
            <div className="content-editor__switch-row">
              <Toggle
                name="sendNotification"
                checked={sendNotification}
                onChange={e => onSendNotificationChange(e.target.checked)}
                label={t('contentEditor.notifySubscribers')}
              />
            </div>
          </div>
        </div>

        <div className="content-editor__settings-row content-editor__settings-row--last">
          <div className="content-editor__settings-meta">
            <div className="content-editor__settings-label">{t('contentEditor.publishingDate')}</div>
            <p className="content-editor__settings-helper">{t('contentEditor.publishingDateHelper')}</p>
          </div>
          <div className="content-editor__settings-field">
            <DatePicker
              format="DD.MM.YYYY"
              value={safePickerValue}
              open={releaseDatePickerOpen}
              onOpen={() => setReleaseDatePickerOpen(true)}
              onClose={() => {
                setReleaseDatePickerOpen(false);
                skipReleaseDatePickerOpenOnFocusRef.current = true;
                if (releaseDatePickerFocusGuardTimeoutRef.current != null) {
                  window.clearTimeout(releaseDatePickerFocusGuardTimeoutRef.current);
                }
                releaseDatePickerFocusGuardTimeoutRef.current = window.setTimeout(() => {
                  releaseDatePickerFocusGuardTimeoutRef.current = null;
                  skipReleaseDatePickerOpenOnFocusRef.current = false;
                }, 150);
              }}
              onChange={date => {
                setReleaseDatePickerOpen(false);
                if (typeof onReleaseDateChange === 'function') {
                  onReleaseDateChange(date && date.format ? date.format('YYYY-MM-DD') : null);
                }
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: 'standard',
                  margin: 'none',
                  onFocus: () => {
                    if (skipReleaseDatePickerOpenOnFocusRef.current) return;
                    setReleaseDatePickerOpen(true);
                  },
                  inputProps: { 'aria-label': t('contentEditor.chooseDateAria') }
                }
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
